import api from '@/lib/axios';
import {
    CustomerLoginRequest,
    CustomerRegisterRequest,
    CustomerAuthResponse,
    CustomerForgotPasswordRequest,
    CustomerResetPasswordRequest,
    CustomerVerifyEmailRequest,
    CustomerResendVerificationRequest,
    CustomerResponse,
    CustomerUpdateProfileRequest
} from '@/types';

export const login = async (data: CustomerLoginRequest): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>('/customer/auth/login', data);
    // Phase 3: the caller (`useAuth.loginMutation`) invokes `setAuth()` on the
    // Zustand store, which is the single source of truth for tokens. We no
    // longer dual-write to raw `accessToken` / `refreshToken` localStorage keys.
    return response.data;
};

export const register = async (data: CustomerRegisterRequest): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>('/customer/auth/register', data);
    // Phase 3: see note in `login()` above. The store handles persistence.
    return response.data;
};

export const refreshToken = async (refreshToken: string): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>('/customer/auth/refresh', {
        refreshToken,
    });
    return response.data;
};

export const logout = async (): Promise<void> => {
    await api.post('/customer/auth/logout');
};

export const getProfile = async (): Promise<CustomerResponse> => {
    const response = await api.get<CustomerResponse>('/customer/auth/profile');
    return response.data;
};

export const updateProfile = async (data: CustomerUpdateProfileRequest): Promise<CustomerResponse> => {
    const response = await api.put<CustomerResponse>('/customer/auth/profile', data);
    return response.data;
};

export const forgotPassword = async (data: CustomerForgotPasswordRequest): Promise<void> => {
    await api.post('/customer/auth/forgot-password', data);
};

export const resetPassword = async (data: CustomerResetPasswordRequest): Promise<void> => {
    await api.post('/customer/auth/reset-password', data);
};

/** Confirm the customer's email with the token from the verification link. Rejects on 400. */
export const verifyEmail = async (data: CustomerVerifyEmailRequest): Promise<void> => {
    await api.post('/customer/auth/verify-email', data);
};

/** Ask for a new verification link. The API always answers 200, whether or not the email exists. */
export const resendVerification = async (data: CustomerResendVerificationRequest): Promise<void> => {
    await api.post('/customer/auth/resend-verification', data);
};

export interface PasswordRequirements {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireDigit: boolean;
    requireNonAlphanumeric: boolean;
}

export const getPasswordRequirements = async (): Promise<PasswordRequirements> => {
    const response = await api.get<PasswordRequirements>('/customer/auth/password-requirements');
    return response.data;
};
